import { use, useCallback, useEffect, useMemo, useRef } from 'react';

import { SuspendedPopoverClickAwayContextObj } from '../context';

export type SuspendedPopoverClickAway =
  | {
      readonly isInPopoverScope: false;
      readonly resume?: never;
      readonly suspend?: never;
    }
  | {
      readonly isInPopoverScope: true;
      readonly resume: () => void;
      readonly suspend: () => void;
    };

/**
 * Returns imperative {@link suspend} / {@link resume} controls that pause the enclosing
 * {@link Popover}'s click-away listener for the lifetime between paired calls.
 *
 * Intended for descendants that render their own portaled menu / popover (such as MUI's
 * {@link Select} or {@link Autocomplete}). Clicks on a descendant's portaled content land outside
 * the {@link Popover}'s DOM subtree and would otherwise be observed by the
 * {@link ClickAwayListener} as "click away" and trigger {@link onClose}. Calling {@link suspend}
 * when the descendant opens and {@link resume} when it closes keeps the {@link Popover} open
 * while the descendant manages its own dismissal.
 *
 * Multiple consumers stack via a ref-counted mechanism on the enclosing {@link Popover}; the
 * click-away listener resumes only when every consumer has released its suspension. When the
 * consumer is rendered without an enclosing {@link Popover} (a `null` context), both methods
 * are no-ops so the consumer can be safely used standalone.
 *
 * {@link suspend} is idempotent within a single consumer instance: calling it twice without an
 * intervening {@link resume} only registers one suspension. {@link resume} is similarly
 * idempotent. The hook also installs an unmount cleanup that releases any outstanding
 * suspension automatically, so callers do not have to handle the unmount-while-suspended case.
 *
 * @returns {{ suspend: () => void; resume: () => void }} Imperative controls intended to be
 *   called from event handlers on the consumer (e.g. {@link RootSelect.onOpen} /
 *   {@link RootSelect.onClose}).
 */
export const useSuspendedPopoverClickAway = (): SuspendedPopoverClickAway => {
  const context = use(SuspendedPopoverClickAwayContextObj);
  const unsuspendRef = useRef<(() => void) | null>(null);

  const isInPopoverScope = context !== null;

  useEffect(
    () => () => {
      unsuspendRef.current?.();
      unsuspendRef.current = null;
    },
    [],
  );

  const suspend = useCallback(() => {
    if (context && !unsuspendRef.current) {
      unsuspendRef.current = context.suspend();
    }
  }, [context]);

  const resume = useCallback((): void => {
    unsuspendRef.current?.();
    unsuspendRef.current = null;
  }, []);

  return useMemo(() => {
    if (isInPopoverScope) {
      return { isInPopoverScope: true, resume, suspend };
    }
    return { isInPopoverScope: false };
  }, [isInPopoverScope, resume, suspend]);
};
